
import React, { useState, useEffect, useRef } from 'react';
import { Property } from '@/types';
import { PropertyCard } from '@/components/broker/PropertyCard';
import { CIEN_CUADRAS_BLUE } from '@/firebase/firebaseConfig';

declare var L: any; // Declare Leaflet global object

interface PublicMapViewProps {
    properties: (Property & { sourceCollection: string })[];
    onViewProperty: (property: Property) => void;
}

const MAP_DEFAULT_CENTER: [number, number] = [4.5709, -74.2973]; // Colombia
const MAP_DEFAULT_ZOOM = 6;
const CLUSTER_PAGE_SIZE = 10;

const createPublicPropertyIcon = (type: string | undefined, isHovered: boolean = false) => {
    let color = '#3b82f6'; // Default blue
    const normalizedType = (type || '').toLowerCase().replace(/\s+/g, '');

    if (normalizedType.includes('casa')) color = '#22c55e';
    else if (normalizedType.includes('oficina')) color = '#8b5cf6';
    else if (normalizedType.includes('local')) color = '#ef4444';

    const size = isHovered ? 26 : 18;
    const borderSize = isHovered ? 3 : 2;

    const iconHtml = `
        <div style="
            background-color: ${color}; 
            width: ${size}px; 
            height: ${size}px; 
            border-radius: 50%; 
            border: ${borderSize}px solid white; 
            box-shadow: 0 1px 3px rgba(0,0,0,0.4);
            transition: all 0.2s ease;
            z-index: ${isHovered ? 1000 : 'auto'};
        "></div>`;
    
    return L.divIcon({
        html: iconHtml,
        className: '', 
        iconSize: [size + borderSize * 2, size + borderSize * 2],
        iconAnchor: [(size + borderSize * 2) / 2, (size + borderSize * 2) / 2]
    });
};

export const PublicMapView: React.FC<PublicMapViewProps> = ({ properties, onViewProperty }) => {
    const mapRef = useRef<HTMLDivElement>(null);
    const [map, setMap] = useState<any | null>(null);
    const [hoveredPropertyId, setHoveredPropertyId] = useState<string | null>(null);
    const [selectedPropertyForCard, setSelectedPropertyForCard] = useState<Property | null>(null);
    const [clusterPropertiesForList, setClusterPropertiesForList] = useState<Property[] | null>(null);
    const [clusterCurrentPage, setClusterCurrentPage] = useState(1);
    const markersRef = useRef<{ [key: string]: any }>({});
    const markersClusterRef = useRef<any>(null);

    useEffect(() => {
        let mapInstance: any = null;
        
        if (mapRef.current && typeof L !== 'undefined') {
            // Ensure any existing map instance on this element is removed
            // Leaflet attaches a _leaflet_id to the element
            if ((mapRef.current as any)._leaflet_id) {
                // This is a fallback if the previous cleanup didn't run
                return; 
            }

            mapInstance = L.map(mapRef.current).setView(MAP_DEFAULT_CENTER, MAP_DEFAULT_ZOOM);
            L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            }).addTo(mapInstance);
            
            setTimeout(() => mapInstance.invalidateSize(), 100);
            setMap(mapInstance);
        }

        return () => {
            if (mapInstance) {
                mapInstance.remove();
                setMap(null);
            }
        };
    }, []); // Run only once on mount

    useEffect(() => {
        if (!map || typeof L === 'undefined') return;

        if (markersClusterRef.current) {
            map.removeLayer(markersClusterRef.current);
        }
        
        markersRef.current = {};
        const propertiesWithCoords = properties.filter(p => p.latitude && p.longitude);
        
        const newMarkerClusterGroup = L.markerClusterGroup();
        markersClusterRef.current = newMarkerClusterGroup;
        
        newMarkerClusterGroup.on('clusterclick', (a: any) => {
            map.fitBounds(a.layer.getBounds(), { padding: [20, 20] });
            const childMarkers = a.layer.getAllChildMarkers();
            const propertyIds = childMarkers.map((m: any) => m.options.propertyId);
            const propertiesInCluster = properties.filter(p => propertyIds.includes(p.id));
            setClusterPropertiesForList(propertiesInCluster);
            setClusterCurrentPage(1);
            setSelectedPropertyForCard(null);
        });
        
        const bounds: [number, number][] = [];

        propertiesWithCoords.forEach(property => {
            const position: [number, number] = [property.latitude!, property.longitude!];
            bounds.push(position);
            const marker = L.marker(position, { icon: createPublicPropertyIcon(property.tipoinmueble), propertyId: property.id });
            marker.on('click', () => { setSelectedPropertyForCard(property); setClusterPropertiesForList(null); map.panTo(position, { animate: true }); });
            marker.on('mouseover', () => setHoveredPropertyId(property.id));
            marker.on('mouseout', () => setHoveredPropertyId(null));
            markersRef.current[property.id] = marker;
            newMarkerClusterGroup.addLayer(marker);
        });

        map.addLayer(newMarkerClusterGroup);
        if (bounds.length > 0) {
            map.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 });
        }
    }, [map, properties]);
    
    useEffect(() => {
        Object.entries(markersRef.current).forEach(([id, marker]) => {
            const isHovered = id === hoveredPropertyId;
            (marker as any).setIcon(createPublicPropertyIcon((marker as any).options.propertyType, isHovered));
            (marker as any).setZIndexOffset(isHovered ? 1000 : 0);
        });
    }, [hoveredPropertyId]);

    const SidePanelContent = () => {
        if (selectedPropertyForCard) {
            return (
                <div>
                    <button onClick={() => setSelectedPropertyForCard(null)} className="mb-3 text-sm font-medium text-sky-600 hover:text-sky-800 hover:underline">&larr; Volver a la lista</button>
                    <PropertyCard property={selectedPropertyForCard} isSelected={false} onView={() => onViewProperty(selectedPropertyForCard)} onShare={() => {}} onToggleSelect={() => {}} isCompact={true} isHighlighted={true} source={(selectedPropertyForCard as any).sourceCollection} appUser={null} onInterested={() => {}} />
                </div>
            );
        }
        if (clusterPropertiesForList) {
            const totalClusterPages = Math.ceil(clusterPropertiesForList.length / CLUSTER_PAGE_SIZE);
            const paginatedClusterList = clusterPropertiesForList.slice((clusterCurrentPage - 1) * CLUSTER_PAGE_SIZE, clusterCurrentPage * CLUSTER_PAGE_SIZE);

            return (
                <div>
                    <button onClick={() => setClusterPropertiesForList(null)} className="mb-3 text-sm font-medium text-sky-600 hover:text-sky-800 hover:underline">&larr; Volver</button>
                    <p className="text-xs text-gray-500 mb-3">Mostrando {paginatedClusterList.length} de {clusterPropertiesForList.length} inmuebles en esta zona.</p>
                    {totalClusterPages > 1 && (
                        <div className="flex justify-between items-center my-3">
                            <button onClick={() => setClusterCurrentPage(p => p - 1)} disabled={clusterCurrentPage === 1} className="px-3 py-1 border rounded-md text-sm">Anterior</button>
                            <span className="text-xs">Página {clusterCurrentPage} / {totalClusterPages}</span>
                            <button onClick={() => setClusterCurrentPage(p => p + 1)} disabled={clusterCurrentPage === totalClusterPages} className="px-3 py-1 border rounded-md text-sm">Siguiente</button>
                        </div>
                    )}
                    <div className="space-y-4">
                        {paginatedClusterList.map(p => <PropertyCard key={p.id} property={p} isSelected={false} onView={() => onViewProperty(p)} onShare={() => {}} onToggleSelect={() => {}} isCompact={true} isHighlighted={selectedPropertyForCard?.id === p.id} onMouseEnter={() => setHoveredPropertyId(p.id)} onMouseLeave={() => setHoveredPropertyId(null)} source={(p as any).sourceCollection} appUser={null} onInterested={() => {}} />)}
                    </div>
                </div>
            );
        }
        return (
            <div className="text-center py-10 px-4 text-gray-600">
                <h3 className="font-semibold" style={{color: CIEN_CUADRAS_BLUE}}>Explora los Inmuebles</h3>
                <p className="text-sm mt-2">Haz clic en un marcador individual para ver su detalle, o en un grupo de marcadores para ver la lista de inmuebles en esa zona.</p>
            </div>
        );
    };

    return (
        <div className="flex flex-col md:flex-row h-[75vh] border-t border-b border-gray-200 relative z-0">
            <div className="h-1/2 md:h-full w-full md:w-3/5" ref={mapRef} role="application" aria-label="Mapa de propiedades compartidas"></div>
            <div className="h-1/2 md:h-full w-full md:w-2/5 overflow-y-auto bg-gray-50 p-3">
                <SidePanelContent />
            </div>
        </div>
    );
};
